import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';

import colors from '@utils/colors';
import fonts, { weights } from '@utils/fonts';
import { mq } from '@utils/responsive';
import UnstyledImage from '@components/Image';
import { fontStyles, Heading, CTA } from '@components/type';

export const BubbleItems = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  padding: 24px 0;
  align-items: flex-start;

  ${mq.gtms`
    gap: 32px;

    ${({ bubblesPerRow }) => css`
      grid-template-columns: repeat(${bubblesPerRow}, minmax(0, 1fr));
    `}
  `}
`;

export const PersonBubbleItem = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  cursor: pointer;
`;

export const SimpleCardBubbleItem = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
`;

export const ImageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  aspect-ratio: 1 / 1;

  ${mq.gtms`
    width: 100%;
    height: auto;
  `}
`;

export const Image = styled(UnstyledImage)`
  border-radius: 50%;

  ${mq.gtms`
    width: 100%;
    height: auto;
  `}
`;

export const Name = styled(Heading)`
  text-transform: uppercase;
  text-align: center;
  margin-top: 24px;
  color: ${colors.B500};
  font-size: 1.25rem;

  ${mq.gtms`
    font-size: 2.25rem;
  `}
`;

export const SimpleTitle = styled(Heading)`
  text-transform: uppercase;
  text-align: center;
  margin-top: 24px;
  color: ${colors.B500};
  font-size: 1.25rem;

  ${mq.gtms`
    font-size: 2.25rem;
  `}
`;

export const Link = styled(CTA)`
  text-transform: uppercase;
  text-align: center;
  color: ${colors.B500};
  text-decoration: underline;
  font-size: 18px;
  margin-top: 12px;
`;

export const Title = styled(Heading)`
  text-transform: uppercase;
  text-align: center;
  margin-top: 8px;
  color: ${colors.B500};
  font-size: 1rem;

  ${mq.gtms`
    font-size: 1.25rem;
  `}
`;

export const SimpleSubtitle = styled(Heading)`
  text-transform: uppercase;
  text-align: center;
  margin-top: 8px;
  color: ${colors.B500};
  font-size: 1rem;

  ${mq.gtms`
    font-size: 1.25rem;
  `}
`;

export const AccordionHeading = styled(Heading)`
  text-transform: uppercase;
  color: ${colors.B500};
  font-size: 1.5rem;
  border-bottom: 1px solid ${colors.B500};
  padding: 20px 0 10px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  margin-bottom: 15px;

  ${mq.gtms`
    font-size: 3rem;
    padding: 30px 0 10px;
  `}
`;

export const PersonBubbleBlockAccordion = styled.div`
  overflow: hidden;
`;

export const AccordionBody = styled.div`
  overflow: hidden;
  max-height: 0;
  opacity: 0;
  transition: max-height 0.5s ease, opacity 0.3s ease;

  &.open {
    max-height: 9999px;
    opacity: 1;
  }
`;

export const IconContainer = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
`;

export const Icon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const SquareImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  aspect-ratio: 1 / 1;

  ${mq.gtms`
    width: 100%;
    height: auto;
  `}
`;

export const SquareImage = styled(UnstyledImage)`
  ${mq.gtms`
    width: 100%;
    height: auto;
  `}
`;

export const ModalWrapper = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 99999;
  background-color: rgba(0, 0, 0, 0.6);
`;

export const Modal = styled(motion.div)`
  position: relative;
  width: 90vw;
  max-width: 500px;
  max-height: 90vh;
  background-color: ${colors.N0};
  overflow-y: auto;
  will-change: transform, opacity;
  padding: 0;

  & > svg {
    position: absolute;
    top: 100%;
    right: 0;
    left: 0;
    transform: translateY(-2px) scale(1, -1);
  }
`;

export const ModalInner = styled.div`
  position: relative;
  padding: 12px;
  overflow: hidden;
`;

export const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 16px;
  right: 24px;
  z-index: 1;
  border: 2px solid ${colors.B100};
  color: ${colors.B500};
  background-color: ${colors.N0};
  border-radius: 3px;
  margin-right: -8px;
  padding: 0;
  appearance: none;
  outline: 0;
  cursor: pointer;

  & > svg {
    height: 24px;
    width: 24px;
    padding: 4px;
  }

  &:hover {
    background-color: ${colors.B100};
  }
`;

export const ModalEmail = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: ${colors.B500};
  font-size: 0.9rem;
  font-weight: ${weights.BOLD};
  line-height: 24px;
  margin-top: 24px;
  padding: 0 40px 24px;
  font-family: ${props => fonts(props.theme.locale).SANS};

  &:before {
    content: '';
    width: 100%;
    height: 1px;
    background-color: ${colors.B500};
    margin-bottom: 20px;
  }
`;

export const ModalName = styled(Heading)`
  text-transform: uppercase;
  text-align: center;
  margin-top: 24px;
  color: ${colors.B500};
  font-size: 1.5rem;

  ${mq.gtms`
    font-size: 2.75rem;
  `}
`;

export const ModalTitle = styled(Heading)`
  text-transform: uppercase;
  text-align: center;
  margin-top: 8px;
  color: ${colors.B500};
  font-size: 1rem;

  ${mq.gtms`
    font-size: 1.75rem;
  `}
`;

export const ModalDescription = styled.div`
  margin: 16px auto;
  font-size: 0.9rem;
  font-family: ${props => fonts(props.theme.locale).SANS};
  color: ${colors.B500};
  text-align: center;
  padding: 0 20px;

  ${mq.gtms`
    margin: 40px auto;
    font-size: 1rem;
  `}

  &:has(ul), &:has(ol) {
    margin-bottom: 0;
  }

  & p {
    ${fontStyles.normal};
  }

  & h1 {
    ${fontStyles.h1};
  }

  & h2 {
    ${fontStyles.h2};
    padding-top: 24px;
  }

  & h3 {
    ${fontStyles.h3};
  }

  & h4 {
    ${fontStyles.h4};
  }

  & blockquote {
    ${fontStyles.blockquote};
  }

  & em {
    ${fontStyles.em};
  }

  & strong {
    ${fontStyles.strong};
  }

  & ul {
    ${fontStyles.normal};
    padding-left: 40px;
    list-style-type: disc;
  }
`;

export const Wrapper = styled.div`
  padding: 18px 24px;

  ${mq.gtms`
    max-width: 1280px;
    margin: 0 auto;
  `}
`;
